import { useState, useRef, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import QRCode from 'qrcode'
import Layout from '../components/Layout'
import CopyButton from '../components/CopyButton'

type Mode = 'encode' | 'decode'
type ErrorCorrectionLevel = 'L' | 'M' | 'Q' | 'H'

function QrCode() {
  const { t } = useTranslation()
  const [mode, setMode] = useState<Mode>('encode')
  const [input, setInput] = useState('')
  const [qrDataUrl, setQrDataUrl] = useState('')
  const [decodedText, setDecodedText] = useState('')
  const [error, setError] = useState('')
  const [errorLevel, setErrorLevel] = useState<ErrorCorrectionLevel>('M')
  const [size, setSize] = useState(256)
  const [darkColor, setDarkColor] = useState('#000000')
  const [lightColor, setLightColor] = useState('#ffffff')
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const generateQR = async (text: string) => {
    setInput(text)
    setError('')
    
    if (!text.trim()) {
      setQrDataUrl('')
      return
    }

    try {
      const dataUrl = await QRCode.toDataURL(text, {
        errorCorrectionLevel: errorLevel,
        width: size,
        margin: 2,
        color: {
          dark: darkColor,
          light: lightColor,
        },
      })
      setQrDataUrl(dataUrl)
    } catch (err) {
      setError(t('toolPages.qrCode.generateError'))
      setQrDataUrl('')
    }
  }

  useEffect(() => {
    if (mode === 'encode' && input) {
      generateQR(input)
    }
  }, [errorLevel, size, darkColor, lightColor])

  const handleModeChange = (newMode: Mode) => {
    setMode(newMode)
    setInput('')
    setQrDataUrl('')
    setDecodedText('')
    setError('')
  }

  const handleClear = () => {
    setInput('')
    setQrDataUrl('')
    setDecodedText('')
    setError('')
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const downloadQR = () => {
    if (!qrDataUrl) return
    
    const link = document.createElement('a')
    link.download = `qrcode-${Date.now()}.png`
    link.href = qrDataUrl
    link.click()
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    setError('')
    setDecodedText('')

    if (!file.type.startsWith('image/')) {
      setError(t('toolPages.qrCode.imageOnlyError'))
      return
    }

    const reader = new FileReader()
    reader.onload = async () => {
      const imageUrl = reader.result as string
      setQrDataUrl(imageUrl)
      
      try {
        const img = new Image()
        img.onload = async () => {
          const canvas = canvasRef.current
          if (!canvas) return
          
          canvas.width = img.width
          canvas.height = img.height
          const ctx = canvas.getContext('2d')
          if (!ctx) return
          
          ctx.drawImage(img, 0, 0)
          
          setDecodedText('')
          setError(t('toolPages.qrCode.scanNotSupported'))
        }
        img.src = imageUrl
      } catch (err) {
        setError(t('toolPages.qrCode.generateError'))
      }
    }
    reader.readAsDataURL(file)
  }

  const loadSample = () => {
    const samples = [
      'https://allinonedecoder.com',
      'Hello, World! 안녕하세요!',
      'WIFI:T:WPA;S:MyNetwork;P:MyPassword;;',
      'mailto:hello@example.com?subject=Hello',
      'tel:+821012345678',
    ]
    const sample = samples[Math.floor(Math.random() * samples.length)]
    generateQR(sample)
  }

  return (
    <Layout showBackButton title={t('toolPages.qrCode.title')}>
      <div className="tool-container">
        <p className="tool-description">
          {t('toolPages.qrCode.description')}
        </p>

      {/* Mode Toggle */}
      <div className="flex flex-wrap gap-4 mb-6">
        <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
          <button
            onClick={() => handleModeChange('encode')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              mode === 'encode'
                ? 'bg-primary-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {t('toolPages.qrCode.generate')}
          </button>
          <button
            onClick={() => handleModeChange('decode')}
            className={`px-4 py-2 text-sm font-medium transition-colors ${
              mode === 'decode'
                ? 'bg-primary-600 text-white'
                : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            {t('toolPages.qrCode.scan')}
          </button>
        </div>

        <div className="flex gap-2">
          {mode === 'encode' && (
            <button onClick={loadSample} className="btn-outline">
              {t('common.sample')}
            </button>
          )}
          <button onClick={handleClear} className="btn-secondary">
            {t('common.clear')}
          </button>
        </div>
      </div>

      {mode === 'encode' ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Input Section */}
          <div className="space-y-6">
            <div className="card">
              <h2 className="section-title mb-3">{t('toolPages.qrCode.inputLabel')}</h2>
              <textarea
                value={input}
                onChange={(e) => generateQR(e.target.value)}
                placeholder={t('toolPages.qrCode.inputPlaceholder')}
                className="textarea h-32 scrollbar-thin"
              />
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {input.length} / 2953 {t('toolPages.qrCode.characters')} ({t('toolPages.qrCode.maxCharacters')})
              </p>
            </div>

            {/* Options */}
            <div className="card">
              <h2 className="section-title mb-4">{t('toolPages.qrCode.options')}</h2>
              
              <div className="space-y-4">
                {/* Error Correction Level */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('toolPages.qrCode.errorLevel')}
                  </label>
                  <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                    {(['L', 'M', 'Q', 'H'] as ErrorCorrectionLevel[]).map((level) => (
                      <button
                        key={level}
                        onClick={() => setErrorLevel(level)}
                        className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                          errorLevel === level
                            ? 'bg-primary-600 text-white'
                            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                        }`}
                      >
                        {level} ({level === 'L' ? '7%' : level === 'M' ? '15%' : level === 'Q' ? '25%' : '30%'})
                      </button>
                    ))}
                  </div>
                </div>

                {/* Size */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('toolPages.qrCode.size')}: {size}px
                  </label>
                  <input
                    type="range"
                    min="128"
                    max="512"
                    step="32"
                    value={size}
                    onChange={(e) => setSize(parseInt(e.target.value))}
                    className="w-full"
                  />
                </div>

                {/* Colors */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('toolPages.qrCode.foreground')}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={darkColor}
                        onChange={(e) => setDarkColor(e.target.value)}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={darkColor}
                        onChange={(e) => setDarkColor(e.target.value)}
                        className="input flex-1"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('toolPages.qrCode.background')}
                    </label>
                    <div className="flex gap-2">
                      <input
                        type="color"
                        value={lightColor}
                        onChange={(e) => setLightColor(e.target.value)}
                        className="w-12 h-10 rounded cursor-pointer"
                      />
                      <input
                        type="text"
                        value={lightColor}
                        onChange={(e) => setLightColor(e.target.value)}
                        className="input flex-1"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Output Section */}
          <div className="card">
            <div className="flex justify-between items-center mb-4">
              <h2 className="section-title">{t('toolPages.qrCode.result')}</h2>
              {qrDataUrl && (
                <button onClick={downloadQR} className="btn-primary">
                  📥 {t('toolPages.qrCode.download')}
                </button>
              )}
            </div>
            
            <div className="flex items-center justify-center min-h-[300px] bg-gray-100 dark:bg-gray-900 rounded-lg p-4">
              {qrDataUrl ? (
                <img
                  src={qrDataUrl}
                  alt="Generated QR Code"
                  className="max-w-full"
                  style={{ imageRendering: 'pixelated' }}
                />
              ) : (
                <p className="text-gray-400 dark:text-gray-500">
                  {t('toolPages.qrCode.enterTextToGenerate')}
                </p>
              )}
            </div>

            {error && (
              <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
                <p className="error-message">{error}</p>
              </div>
            )}
          </div>
        </div>
      ) : (
        /* Decode Mode */
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Upload Section */}
          <div className="card">
            <h2 className="section-title mb-3">{t('toolPages.qrCode.uploadImage')}</h2>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="input"
            />
            
            {qrDataUrl && (
              <div className="mt-4 flex items-center justify-center bg-gray-100 dark:bg-gray-900 rounded-lg p-4">
                <img
                  src={qrDataUrl}
                  alt="Uploaded QR Code"
                  className="max-w-full max-h-64"
                />
              </div>
            )}

            <canvas ref={canvasRef} className="hidden" />

            {error && (
              <div className="mt-3 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                <p className="text-yellow-700 dark:text-yellow-400 text-sm">{error}</p>
              </div>
            )}
          </div>

          {/* Result Section */}
          <div className="card">
            <div className="flex justify-between items-center mb-3">
              <h2 className="section-title">{t('toolPages.qrCode.decodingResult')}</h2>
              {decodedText && <CopyButton text={decodedText} />}
            </div>
            <textarea
              value={decodedText}
              readOnly
              placeholder={t('toolPages.qrCode.uploadPlaceholder')}
              className="textarea h-48 bg-gray-50 dark:bg-gray-900 scrollbar-thin"
            />
          </div>
        </div>
      )}
      </div>
    </Layout>
  )
}

export default QrCode
